"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { InlineMath, BlockMath } from "react-katex"
import "katex/dist/katex.min.css"
import { Loader2, Plus, Pencil, Trash, Save, ImageIcon, BookOpen } from "lucide-react"
import { postAws } from "../postAws"
import { postTeacherAssignmentQuestionImage, generatePresignedUrl } from "../postFileAws"

export default function CreateAssignmentManually() {
  const [assignment, setAssignment] = useState({
    assignmentQuestion: [],
    assignment_topic: "",
    assignmentTitle: "",
    assignmentDescription: "",
    assignmentDueDate: "",
    classRoomId: "",
    teacherId: "",
    shareWithStudents: false,
    maxMarks: "0",
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState(-1)
  const [editedQuestion, setEditedQuestion] = useState({})
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [newQuestion, setNewQuestion] = useState({
    questionType: "Essay Type",
    question: "",
    questionTopic: "",
    questionDifficulty: "Easy",
    mcqOptions: [],
    maxMarks: 0,
    keyPoints: "", // Changed from correct_answer to keyPoints
    questionImageUrl: null,
    questionImageUri: null,
  })
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, index: -1 })
  const [imageUrls, setImageUrls] = useState({})
  const [imageLoading, setImageLoading] = useState({})
  const navigate = useNavigate()

  const teacherId = localStorage.getItem("teacherId")
  const classRoomId = localStorage.getItem("classRoomId")
  const classCode = localStorage.getItem("classCode")

  useEffect(() => {
    const fetchAssignment = async () => {
      setLoading(true)
      try {
        const assignmentData = localStorage.getItem("assignmentData")

        if (!assignmentData) {
          throw new Error("No assignment data found in localStorage")
        }
        const parsedData = JSON.parse(assignmentData)
        console.log("Fetched assignment data:", parsedData)
        setAssignment((prev) => ({
          ...prev,
          assignment_topic: parsedData.assignment_topic || "",
          assignmentTitle: parsedData.assignmentTitle || "",
          assignmentDueDate: parsedData.assignmentDueDate || "",
          classRoomId: parsedData.classRoomId || "",
          teacherId: parsedData.teacherId || "",
          assignmentDescription: parsedData.assignmentDescription || "",
        }))

        // Generate presigned URLs for images
        const urls = {}
        const loadingState = {}
        for (const question of parsedData.assignmentQuestion || []) {
          if (question.questionImageUrl) {
            loadingState[question.id] = true
            setImageLoading((prev) => ({ ...prev, [question.id]: true }))
            try {
              const url = await generatePresignedUrl(question.questionImageUrl)
              urls[question.id] = url
              loadingState[question.id] = false
            } catch (error) {
              console.error("Error generating presigned URL:", error)
              loadingState[question.id] = false
            }
          }
        }
        setImageUrls(urls)
        setImageLoading(loadingState)
      } catch (err) {
        console.error("Error fetching assignment data:", err)
        setError(err.message || "An unknown error occurred")
        setAssignment({ assignmentQuestion: [] })
      } finally {
        setLoading(false)
      }
    }

    fetchAssignment()
  }, [])

  const renderMathText = (text) => {
    if (!text) return null
    const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/)
    return parts.map((part, i) => {
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return <BlockMath key={i}>{part.slice(2, -2)}</BlockMath>
      } else if (part.startsWith("$") && part.endsWith("$")) {
        return <InlineMath key={i}>{part.slice(1, -1)}</InlineMath>
      } else {
        return <span key={i}>{part}</span>
      }
    })
  }

  const handleSaveAssignment = async (share = false) => {
    setIsSaveModalOpen(false)
    setLoading(true)

    const totalMarks = assignment.assignmentQuestion.reduce((total, q) => total + (Number.parseInt(q.maxMarks) || 0), 0)

    const assignmentData = {
      body: {
        assignment_topic: assignment.assignmentTitle || "",
        assignmentTitle: assignment.assignmentTitle || "",
        classCode: assignment.classCode || classCode,
        classRoomId: assignment.classRoomId || classRoomId,
        teacherId: assignment.teacherId || teacherId,
        assignmentDescription: assignment.assignmentDescription || "",
        assignmentDueDate: assignment.assignmentDueDate || "",
        assignmentQuestion: assignment.assignmentQuestion.map((q) => ({
          ...q,
          maxMarks: Number.parseInt(q.maxMarks) || 0,
        })),
        shareWithStudents: share,
        maxMarks: totalMarks.toString(),
      },
      request: "create-Assignment",
    }

    console.log("Sending assignment data:", assignmentData)

    try {
      const response = await postAws("https://api-dev.aurait.ai/assignment", "POST", assignmentData)
      console.log("Server response:", response)

      if (response.statusCode === 201) {
        localStorage.removeItem("assignmentData")
        setTimeout(() => {
          setLoading(false)
          navigate("/assignments")
        }, 2000)
      } else {
        throw new Error(response.body ? JSON.parse(response.body).message : "Failed to update assignment")
      }
    } catch (error) {
      console.error("Error updating assignment:", error)
      setLoading(false)
      setError(error.message || "Failed to save assignment. Please try again.")
    }
  }

  const handleDelete = (index) => {
    setDeleteConfirmation({ isOpen: true, index })
  }

  const confirmDelete = () => {
    setAssignment((prev) => ({
      ...prev,
      assignmentQuestion: (prev.assignmentQuestion || []).filter((_, i) => i !== deleteConfirmation.index),
    }))
    setDeleteConfirmation({ isOpen: false, index: -1 })
  }

  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, index: -1 })
  }

  const handleEdit = (index) => {
    const questionToEdit = assignment.assignmentQuestion?.[index]
    if (questionToEdit) {
      setEditingIndex(index)
      setEditedQuestion({
        ...questionToEdit,
        questionType: questionToEdit.questionType,
      })
      setIsEditModalOpen(true)
    } else {
      console.error(`Question at index ${index} not found`)
    }
  }

  const handleSaveEdit = () => {
    if (editingIndex !== -1) {
      if (!editedQuestion.question.trim()) {
        alert("Error: Question is compulsory. Please enter a question.")
        return
      }
      if (!editedQuestion.keyPoints.trim()) {
        alert("Error: Key points are compulsory. Please enter key points for evaluation.")
        return
      }
      if (
        editedQuestion.questionType === "MCQ" &&
        editedQuestion.mcqOptions.filter((option) => option.trim()).length < 2
      ) {
        alert("Error: For MCQ, please add at least 2 options.")
        return
      }
      setAssignment((prev) => ({
        ...prev,
        assignmentQuestion: (prev.assignmentQuestion || []).map((q, i) =>
          i === editingIndex ? { ...editedQuestion, maxMarks: editedQuestion.maxMarks || 0 } : q,
        ),
      }))
    }
    setIsEditModalOpen(false)
    setEditingIndex(-1)
    setEditedQuestion({})
  }

  const handleAddQuestion = () => {
    setIsAddModalOpen(true)
  }

  const handleSaveNewQuestion = () => {
    if (!newQuestion.question.trim()) {
      alert("Error: Question is compulsory. Please enter a question.")
      return
    }
    if (!newQuestion.keyPoints.trim()) {
      alert("Error: Key points are compulsory. Please enter key points for evaluation.")
      return
    }
    if (newQuestion.questionType === "MCQ" && newQuestion.mcqOptions.filter((option) => option.trim()).length < 2) {
      alert("Error: For MCQ, please add at least 2 options.")
      return
    }

    const newQuestionId = `new-question-${Date.now()}`
    const newQuestionWithImage = {
      ...newQuestion,
      id: newQuestionId,
      questionImageUrl: newQuestion.questionImageUrl,
      questionImageUri: newQuestion.questionImageUri,
    }
    setAssignment((prev) => ({
      ...prev,
      assignmentQuestion: [...(prev.assignmentQuestion || []), newQuestionWithImage],
    }))
    setImageUrls((prev) => {
      if (prev["newQuestion"]) {
        return {
          ...prev,
          [newQuestionId]: prev["newQuestion"],
        }
      }
      return prev
    })
    setIsAddModalOpen(false)
    setNewQuestion({
      questionType: "Essay Type",
      question: "",
      questionTopic: "",
      questionDifficulty: "Easy",
      mcqOptions: [],
      maxMarks: 0,
      keyPoints: "",
      questionImageUrl: null,
      questionImageUri: null,
    })
  }

  const handleImageUpload = async (event, isNewQuestion = true) => {
    const file = event.target.files?.[0]
    if (file) {
      setIsUploading(true)
      try {
        const assignmentName = assignment.assignment_topic || "Untitled Assignment"
        const questionNumber = isNewQuestion
          ? `q${(assignment.assignmentQuestion || []).length + 1}`
          : `q${editingIndex + 1}`
        const filename = file.name

        const result = await postTeacherAssignmentQuestionImage(
          file,
          teacherId,
          assignmentName,
          filename,
          questionNumber,
          classRoomId,
        )

        const presignedUrl = await generatePresignedUrl(result.uri)

        if (isNewQuestion) {
          setNewQuestion((prev) => ({
            ...prev,
            questionImageUrl: result.url,
            questionImageUri: result.uri,
          }))
          setImageUrls((prev) => ({
            ...prev,
            newQuestion: presignedUrl,
          }))
        } else {
          setEditedQuestion((prev) => ({
            ...prev,
            questionImageUrl: result.url,
            questionImageUri: result.uri,
          }))
          setImageUrls((prev) => ({
            ...prev,
            [editedQuestion.id]: presignedUrl,
          }))
        }
      } catch (error) {
        console.error("Error uploading image:", error)
        alert("Failed to upload image. Please try again.")
      } finally {
        setIsUploading(false)
      }
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <Loader2 className="animate-spin h-16 w-16 text-blue-500 mx-auto" />
          <p className="text-center mt-4">Loading assignment data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
          {assignment.assignment_topic || "Create Assignment"}
        </h1>

        <div className="flex justify-between mb-6">
          <button
            onClick={handleAddQuestion}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
          >
            <Plus size={20} />
            <span>Add Question</span>
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => setIsSaveModalOpen(true)}
              className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
            >
              <Save size={20} />
              <span>Save Assignment</span>
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {!assignment?.assignmentQuestion?.length && (
            <div className="w-full text-center text-2xl text-gray-700 bg-gray-100 py-16">
              No questions yet, add a question
            </div>
          )}
          {assignment.assignmentQuestion &&
            assignment.assignmentQuestion.map((question, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm mb-4 border border-gray-200">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-gray-700 mb-2">Question {index + 1}</h2>
                    <div className="text-gray-600 whitespace-pre-wrap mb-4">{renderMathText(question.question)}</div>

                    {question.questionTopic && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded">
                          Topic: {question.questionTopic}
                        </span>
                      </div>
                    )}

                    {question.questionDifficulty && (
                      <div className="mb-4">
                        <span
                          className={`text-sm font-medium px-2 py-1 rounded ${
                            question.questionDifficulty === "Easy"
                              ? "text-green-600 bg-green-100"
                              : question.questionDifficulty === "Medium"
                                ? "text-yellow-600 bg-yellow-100"
                                : "text-red-600 bg-red-100"
                          }`}
                        >
                          Difficulty: {question.questionDifficulty}
                        </span>
                      </div>
                    )}

                    {question.questionType === "MCQ" && question.mcqOptions && question.mcqOptions.length > 0 && (
                      <div className="mt-2 space-y-2 mb-4">
                        <h4 className="text-sm font-medium text-gray-700">Answer Options:</h4>
                        {question.mcqOptions
                          .filter((opt) => opt.trim())
                          .map((option, i) => (
                            <div key={i} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                              <span className="font-medium text-gray-600 min-w-[20px]">
                                {String.fromCharCode(65 + i)}.
                              </span>
                              <span className="text-gray-700">{renderMathText(option)}</span>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Key Points Section */}
                    {question.keyPoints && (
                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
                          <BookOpen size={16} />
                          Key Points for Evaluation:
                        </h4>
                        <div className="text-green-700 whitespace-pre-wrap text-sm">
                          {renderMathText(question.keyPoints)}
                        </div>
                      </div>
                    )}

                    {question.questionImageUrl && (
                      <div className="mt-4">
                        {imageLoading[question.id] ? (
                          <div className="flex items-center justify-center h-32 bg-gray-100 rounded-lg">
                            <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
                            <span className="ml-2 text-sm text-gray-600">Loading image...</span>
                          </div>
                        ) : (
                          <img
                            src={imageUrls[question.id] || question.questionImageUrl}
                            alt="Question"
                            className="max-w-full h-auto rounded-lg shadow-sm"
                            onError={(e) => {
                              e.target.onerror = null
                              e.target.src = "/placeholder.svg"
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <div className="flex items-center gap-2">
                      <label htmlFor={`marks-${index}`} className="text-sm text-gray-600">
                        Marks:
                      </label>
                      <input
                        id={`marks-${index}`}
                        type="number"
                        value={question.maxMarks || 0}
                        onChange={(e) => {
                          const newMarks = Number.parseInt(e.target.value, 10)
                          setAssignment((prev) => ({
                            ...prev,
                            assignmentQuestion: (prev.assignmentQuestion || []).map((q, i) =>
                              i === index ? { ...q, maxMarks: newMarks } : q,
                            ),
                          }))
                        }}
                        className="w-16 px-2 py-1 border rounded text-right"
                        min="0"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(index)}
                        className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                        aria-label="Edit question"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(index)}
                        className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        aria-label="Delete question"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

        <div className="mt-8 text-right">
          <p className="text-xl font-bold text-gray-800">
            Total Marks:{" "}
            {assignment.assignmentQuestion
              ? assignment.assignmentQuestion.reduce((total, q) => total + (q.maxMarks || 0), 0)
              : 0}
          </p>
        </div>
      </div>

      {/* Edit Question Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto">
          <div className="bg-white p-6 rounded-lg w-full max-w-md m-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Question</h2>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={editedQuestion.questionType || "Essay Type"}
                  onChange={(e) => setEditedQuestion({ ...editedQuestion, questionType: e.target.value })}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="Essay Type">Essay Type</option>
                  <option value="Short Answer">Short Answer</option>
                  <option value="MCQ">MCQ</option>
                </select>
              </div>
              <div>
                <label htmlFor="questionDifficulty" className="block text-sm font-medium text-gray-700 mb-1">
                  Difficulty
                </label>
                <select
                  id="questionDifficulty"
                  value={editedQuestion.questionDifficulty || "Easy"}
                  onChange={(e) => setEditedQuestion({ ...editedQuestion, questionDifficulty: e.target.value })}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="questionTopic" className="block text-sm font-medium text-gray-700 mb-1">
                Topic
              </label>
              <input
                type="text"
                id="questionTopic"
                value={editedQuestion.questionTopic || ""}
                onChange={(e) => setEditedQuestion({ ...editedQuestion, questionTopic: e.target.value })}
                className="w-full p-2 border rounded text-sm"
                placeholder="Enter question topic"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 mb-1">
                Question *
              </label>
              <textarea
                id="questionText"
                value={editedQuestion.question || ""}
                onChange={(e) => setEditedQuestion({ ...editedQuestion, question: e.target.value })}
                className="w-full h-24 p-2 border rounded text-sm"
                placeholder="Enter question text"
              />
            </div>

            {editedQuestion.questionType === "MCQ" && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Answer Options</label>
                {(editedQuestion.mcqOptions || []).map((option, index) => (
                  <div key={index} className="flex mb-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...(editedQuestion.mcqOptions || [])]
                        newOptions[index] = e.target.value
                        setEditedQuestion({ ...editedQuestion, mcqOptions: newOptions })
                      }}
                      placeholder={`Option ${index + 1}`}
                      className="flex-grow p-2 border rounded text-sm"
                    />
                    {index > 1 && (
                      <button
                        onClick={() => {
                          const newOptions = (editedQuestion.mcqOptions || []).filter((_, i) => i !== index)
                          setEditedQuestion({ ...editedQuestion, mcqOptions: newOptions })
                        }}
                        className="ml-2 px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                      >
                        -
                      </button>
                    )}
                  </div>
                ))}
                {(editedQuestion.mcqOptions || []).length < 6 && (
                  <button
                    onClick={() => {
                      const newOptions = [...(editedQuestion.mcqOptions || []), ""]
                      setEditedQuestion({ ...editedQuestion, mcqOptions: newOptions })
                    }}
                    className="w-full px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                  >
                    Add Option
                  </button>
                )}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="keyPoints" className="block text-sm font-medium text-gray-700 mb-1">
                Key Points for Evaluation *
              </label>
              <textarea
                id="keyPoints"
                value={editedQuestion.keyPoints || ""}
                onChange={(e) => setEditedQuestion({ ...editedQuestion, keyPoints: e.target.value })}
                className="w-full h-20 p-2 border rounded text-sm"
                placeholder="Enter key points that should be covered in the answer for evaluation purposes"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="maxMarks" className="block text-sm font-medium text-gray-700 mb-1">
                Max Marks
              </label>
              <input
                type="number"
                id="maxMarks"
                value={editedQuestion.maxMarks || 0}
                onChange={(e) => setEditedQuestion({ ...editedQuestion, maxMarks: Number(e.target.value) })}
                className="w-full p-2 border rounded text-sm"
                min="0"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="imageUpload" className="block text-sm font-medium text-gray-700 mb-2">
                Question Image
              </label>
              <div className="mt-1 flex items-center">
                <span className="inline-block h-24 w-24 rounded-lg overflow-hidden bg-gray-100">
                  {editedQuestion.questionImageUrl && !isUploading ? (
                    <img
                      src={imageUrls[editedQuestion.id] || editedQuestion.questionImageUrl}
                      alt="Question"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null
                        e.target.src = "/placeholder.svg"
                      }}
                    />
                  ) : (
                    <ImageIcon className="h-24 w-24 text-gray-300" />
                  )}
                </span>
                <input
                  type="file"
                  id="imageUpload"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, false)}
                  className="hidden"
                />
                <label
                  htmlFor="imageUpload"
                  className="ml-5 bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {editedQuestion.questionImageUrl ? "Change" : "Upload"}
                </label>
                {editedQuestion.questionImageUrl && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditedQuestion((prev) => ({ ...prev, questionImageUrl: null, questionImageUri: null }))
                    }}
                    className="ml-3 bg-red-100 py-2 px-3 border border-transparent rounded-md shadow-sm text-sm leading-4 font-medium text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Remove
                  </button>
                )}
              </div>
              {isUploading && (
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <Loader2 className="animate-spin mr-2" size={16} />
                  <span>Uploading image...</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Question Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto">
          <div className="bg-white p-6 rounded-lg w-full max-w-md m-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Add New Question</h2>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newQuestion.questionType}
                  onChange={(e) => setNewQuestion({ ...newQuestion, questionType: e.target.value })}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="Essay Type">Essay Type</option>
                  <option value="Short Answer">Short Answer</option>
                  <option value="MCQ">MCQ</option>
                </select>
              </div>
              <div>
                <label htmlFor="newQuestionDifficulty" className="block text-sm font-medium text-gray-700 mb-1">
                  Difficulty
                </label>
                <select
                  id="newQuestionDifficulty"
                  value={newQuestion.questionDifficulty}
                  onChange={(e) => setNewQuestion({ ...newQuestion, questionDifficulty: e.target.value })}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="newQuestionTopic" className="block text-sm font-medium text-gray-700 mb-1">
                Topic
              </label>
              <input
                type="text"
                id="newQuestionTopic"
                value={newQuestion.questionTopic}
                onChange={(e) => setNewQuestion({ ...newQuestion, questionTopic: e.target.value })}
                className="w-full p-2 border rounded text-sm"
                placeholder="Enter question topic"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="newQuestionText" className="block text-sm font-medium text-gray-700 mb-1">
                Question *
              </label>
              <textarea
                id="newQuestionText"
                value={newQuestion.question}
                onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                className="w-full h-24 p-2 border rounded text-sm"
                placeholder="Enter question text"
              />
            </div>

            {newQuestion.questionType === "MCQ" && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Answer Options</label>
                {newQuestion.mcqOptions.map((option, index) => (
                  <div key={index} className="flex mb-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...newQuestion.mcqOptions]
                        newOptions[index] = e.target.value
                        setNewQuestion({ ...newQuestion, mcqOptions: newOptions })
                      }}
                      placeholder={`Option ${index + 1}`}
                      className="flex-grow p-2 border rounded text-sm"
                    />
                    {index > 1 && (
                      <button
                        onClick={() => {
                          const newOptions = newQuestion.mcqOptions.filter((_, i) => i !== index)
                          setNewQuestion({ ...newQuestion, mcqOptions: newOptions })
                        }}
                        className="ml-2 px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                      >
                        -
                      </button>
                    )}
                  </div>
                ))}
                {newQuestion.mcqOptions.length < 6 && (
                  <button
                    onClick={() => {
                      const newOptions = [...newQuestion.mcqOptions, ""]
                      setNewQuestion({ ...newQuestion, mcqOptions: newOptions })
                    }}
                    className="w-full px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                  >
                    Add Option
                  </button>
                )}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="newKeyPoints" className="block text-sm font-medium text-gray-700 mb-1">
                Key Points for Evaluation *
              </label>
              <textarea
                id="newKeyPoints"
                value={newQuestion.keyPoints}
                onChange={(e) => setNewQuestion({ ...newQuestion, keyPoints: e.target.value })}
                className="w-full h-20 p-2 border rounded text-sm"
                placeholder="Enter key points that should be covered in the answer for evaluation purposes"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="newMaxMarks" className="block text-sm font-medium text-gray-700 mb-1">
                Max Marks
              </label>
              <input
                type="number"
                id="newMaxMarks"
                value={newQuestion.maxMarks}
                onChange={(e) => setNewQuestion({ ...newQuestion, maxMarks: Number(e.target.value) })}
                className="w-full p-2 border rounded text-sm"
                min="0"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="newImageUpload" className="block text-sm font-medium text-gray-700 mb-2">
                Question Image
              </label>
              <div className="mt-1 flex items-center">
                <span className="inline-block h-24 w-24 rounded-lg overflow-hidden bg-gray-100">
                  {newQuestion.questionImageUrl && !isUploading ? (
                    <img
                      src={imageUrls["newQuestion"] || newQuestion.questionImageUrl}
                      alt="Question"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null
                        e.target.src = "/placeholder.svg"
                      }}
                    />
                  ) : (
                    <ImageIcon className="h-24 w-24 text-gray-300" />
                  )}
                </span>
                <input
                  type="file"
                  id="newImageUpload"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, true)}
                  className="hidden"
                />
                <label
                  htmlFor="newImageUpload"
                  className="ml-5 bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {newQuestion.questionImageUrl ? "Change" : "Upload"}
                </label>
                {newQuestion.questionImageUrl && (
                  <button
                    type="button"
                    onClick={() => {
                      setNewQuestion((prev) => ({ ...prev, questionImageUrl: null, questionImageUri: null }))
                      setImageUrls((prev) => {
                        const newUrls = { ...prev }
                        delete newUrls["newQuestion"]
                        return newUrls
                      })
                    }}
                    className="ml-3 bg-red-100 py-2 px-3 border border-transparent rounded-md shadow-sm text-sm leading-4 font-medium text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Remove
                  </button>
                )}
              </div>
              {isUploading && (
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <Loader2 className="animate-spin mr-2" size={16} />
                  <span>Uploading image...</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNewQuestion}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
              >
                Add Question
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Modal */}
      {isSaveModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Save Assignment</h2>
            <p className="mb-4">Are you sure you want to save this assignment?</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsSaveModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveAssignment(false)}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
              >
                Confirm Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this question?</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Modal */}
      {loading && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <Loader2 className="animate-spin h-16 w-16 text-blue-500 mx-auto" />
            <p className="text-center mt-4">Processing...</p>
          </div>
        </div>
      )}
    </div>
  )
}
